export type EnquiryData = {
    _id?: string;
    dateOfEnquiry?: string;
    institutionName?: string;
    enquiryPurpose?: string;
    enquiryAmount?: string;
    tenure?: string;
    directorId?: string;
    assesseeId?: string;
};

export type BureauEnquiryTableProps = {
    data: EnquiryData[];
    loading: boolean;
    vendorType: VendorType;
};

export type VendorType = 'EQUIFAX' | 'EXPERIAN' | 'TRANSUNION';

// Bureau-type enum-like constant. Use BureauType.X instead of inline string literals
// for type-safety + grep-ability. Includes ALL (the "no specific bureau" filter value)
// alongside the three real bureaus.
export const BureauType = {
    EQUIFAX: 'EQUIFAX',
    EXPERIAN: 'EXPERIAN',
    TRANSUNION: 'TRANSUNION',
    ALL: 'All',
} as const;
export type BureauTypeValue = (typeof BureauType)[keyof typeof BureauType];

export const NEW_TO_CREDIT_THRESHOLD = 100;

// Maximum possible bureau score (denominator for the score progress bars).
export const MAX_BUREAU_SCORE = 900;

export const REFRESH_BUREAU_CTA = 'Refresh Bureau';

/** The all-directors row in Key Officials. Referenced from its tests, so it lives here. */
export const COMBINED_DIRECTOR_REPORT_LABEL = 'Combined Director Report';

export const UNIFIED_REPORT_ALL_TAB_HINT = 'Select an individual bureau from the toggle above to view combined reports of directors.';

/** How far back the Director Bureau enquiry banner counts enquiries. */
export const ENQUIRY_LOOKBACK_YEARS = 1;

export const ENQUIRY_BANNER_LINK_LABEL = 'View Details';

/**
 * The banner is the only entry point to the enquiry drawer, and the drawer shows full history with
 * no lookback — so it has to appear whenever there are any enquiries at all, not just recent ones.
 * The lookback only decides which sentence it gets.
 */
export function enquiryBannerText(recentCount: number, totalCount: number): string {
    const count = recentCount > 0 ? recentCount : totalCount;
    const subject = `There ${count === 1 ? 'is' : 'are'} ${count} Bureau ${count === 1 ? 'Enquiry' : 'Enquiries'}`;

    return recentCount > 0 ? `${subject} in the last ${ENQUIRY_LOOKBACK_YEARS} year.` : `${subject} on record.`;
}

/**
 * Bureaus that have a Credit Analysis workbook renderer behind them.
 *
 * The workbook is not bureau-agnostic: each bureau has its own status
 * vocabulary and code maps, and grading one bureau's file under another's
 * rules reads as a clean payment history rather than an error. So a bureau
 * only appears here once its renderer and ingestion have been verified.
 */
export const WORKBOOK_BUREAUS: string[] = [BureauType.TRANSUNION, BureauType.EQUIFAX, BureauType.EXPERIAN];

export const hasWorkbook = (bureau?: string | null): boolean => !!bureau && WORKBOOK_BUREAUS.includes(bureau);

/**
 * Whether to offer the Bureau Query (need-info sheet) CTA — the complement of
 * hasWorkbook, for a chosen bureau that has no workbook renderer yet.
 *
 * Exported rather than inlined so the component and its test share one
 * definition; a test that restates the condition cannot catch the condition
 * changing. The truthy check is load-bearing: the bureau type comes from a query
 * param and is null until a bureau is chosen, and null satisfies both
 * `!== ALL` and `!hasWorkbook`, so omitting it renders this CTA on first paint.
 */
export const hasBureauQueryCta = (bureau?: string | null): boolean => !!bureau && bureau !== BureauType.ALL && !hasWorkbook(bureau);

/**
 * The bureau a director report actually belongs to.
 *
 * The `bureauType` param is ALL on the all-bureaux tab, which is not a bureau —
 * but a director row on that tab still belongs to exactly one, carried in
 * `directorBureauType`. Anything gating on the report's real bureau must use
 * this: hasWorkbook('All') and hasWorkbook(null) are both false, so reading
 * `bureauType` alone hid the Credit Analysis CTA for every director opened from
 * the ALL tab.
 *
 * Exported rather than inlined in the hook so the component and its test share
 * one definition.
 */
export const resolveBureauType = (bureauTypeParam?: string | null, directorBureauTypeParam?: string | null): string | null => {
    const explicit = bureauTypeParam === BureauType.ALL ? null : bureauTypeParam;
    return explicit || directorBureauTypeParam || null;
};
